import SearchBar from 'components/SearchBar/SearchBar';
import CustomSlider from 'components/Sliders/CustomSlider';
import Toggle from 'components/UI/Toggle';
import { DEFAULT_SELECTED_ALERT_SOURCES } from 'features/Weather/features/WeatherAlerts/config/alertSources.config';
import AlertSortSelector from 'features/Weather/features/WeatherAlerts/features/AlertList/components/AlertSortSelector/AlertSortSelector';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectListConfig,
  selectMaxDist,
  selectShowLocalOnly,
} from '../../state/selectors/alertListSelectors';
import {
  setListConfig,
  setMaxDist,
  setPageIndex,
  setSearchTerm,
  setShowLocalOnly,
} from '../../state/slices/alertListSlice';
import AlertSourceFilter from '../AlertSourceFilter/AlertSourceFilter';
import AlertTypeFilter from '../AlertTypeFilter/AlertTypeFilter';

/**
 * Renders the Alert List filters and severity sort control.
 *
 * @param {{title: string, perms: {testPermission: Function}}} props - Header title and permissions.
 * @returns {JSX.Element} The Alert List header.
 */
const AlertListHeader = ({ title, perms }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listConfig = useSelector(selectListConfig);
  const showLocalOnly = useSelector(selectShowLocalOnly);
  const maxDist = useSelector(selectMaxDist);

  return (
    <div>
      <div className="d-flex flex-column align-items-center flex-xl-row justify-content-md-between mb-3">
        <div className="d-flex flex-column align-items-center flex-fill flex-xl-row gap-3 gap-xl-5">
          <div className="fs-4 fw-bold">{title}</div>
          <SearchBar
            className="w-auto d-flex"
            ariaLabel="Search alerts"
            placeholder="Search alerts"
            value={listConfig.searchTerm ?? ''}
            onChange={(searchTerm) => {
              dispatch(setSearchTerm(searchTerm));
              dispatch(setPageIndex(1));
            }}
          />
          <AlertSourceFilter
            selectedSources={listConfig.selectedSources ?? DEFAULT_SELECTED_ALERT_SOURCES}
            onChange={(selectedSources) => {
              dispatch(setListConfig({ selectedSources }));
              dispatch(setPageIndex(1));
            }}
          />
          <AlertTypeFilter
            selectedSources={listConfig.selectedSources ?? DEFAULT_SELECTED_ALERT_SOURCES}
            selectedTypesBySource={listConfig.alertTypesBySource ?? {}}
            onChange={(alertTypesBySource) => {
              dispatch(setListConfig({ alertTypesBySource }));
              dispatch(setPageIndex(1));
            }}
          />
        </div>
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditAlerts']) && (
          <Button
            className="px-3 rounded-3 mt-3 mt-xl-0 ms-xl-auto flex-shrink-0 text-nowrap"
            onClick={() => navigate('/publicuser/createAlert')}
            variant="primary"
          >
            Create Alert
          </Button>
        )}
      </div>
      {/* Show Local Switch */}
      <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
        <AlertSortSelector />
        <div className="d-flex gap-2 align-content-center align-items-center">
          {showLocalOnly && (
            <div style={{ marginLeft: 20 }}>
              Max Distance (mi):{' '}
              <input
                type="number"
                min={0}
                max={500}
                value={maxDist}
                onChange={(e) => {
                  dispatch(setPageIndex(1));
                  dispatch(setMaxDist(e.target.value));
                }}
              />
              <CustomSlider
                value={maxDist}
                onChange={(value) => {
                  dispatch(setPageIndex(1));
                  dispatch(setMaxDist(value));
                }}
                min={0}
                max={500}
                step={5}
                style={{ width: 150 }}
              />
            </div>
          )}
          <Toggle
            value={showLocalOnly}
            callback={(value) => {
              dispatch(setShowLocalOnly(value));
              dispatch(setPageIndex(1));
            }}
            label="Show Local Only"
          />
        </div>
      </div>
    </div>
  );
};

export default AlertListHeader;
